import { Component, OnInit, ChangeDetectorRef, Output, EventEmitter, ViewChild, TemplateRef, inject, OnDestroy, AfterViewInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { Professional } from '../../../../core/models/professional.model';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { TranslateModule } from '@ngx-translate/core';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, startWith, switchMap, tap, of, Subscription } from 'rxjs';
import { toast } from '../../../../shared/utils/toast';
import { TranslateService } from '@ngx-translate/core';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { SettingsService } from '../../../../core/services/settings.service';
import { groupSpecialities, withSelectedOnTop, GroupedSpecialities, SpecialityGroupOption, isGroupFullySelected, toggleGroupSelection } from '../../../../shared/utils/speciality-groups.util';

@Component({
    selector: 'app-list-of-doctors',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AppPrimeTable,
        TranslateModule,
        AppDropdown,
        AppButton,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        RouterModule,
        HasPermissionDirective,
        ConfirmDialogModule,
        MultiSelectModule
    ],
    providers: [ConfirmationService],
    templateUrl: './list-of-doctors.html',
})
export class ListOfDoctors implements OnInit, AfterViewInit, OnDestroy {
    private subs = new Subscription();
    @Output() newDoctor = new EventEmitter<void>();
    @Output() editDoctor = new EventEmitter<Professional>();
    @Output() viewDoctor = new EventEmitter<Professional>();

    @ViewChild('doctorNameTemplate') doctorNameTemplate!: TemplateRef<any>;
    @ViewChild('statusTemplate') statusTemplate!: TemplateRef<any>;
    @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<any>;

    private professionalsService = inject(ProfessionalsService);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private confirmationService = inject(ConfirmationService);
    private store = inject(Store);
    private settingsService = inject(SettingsService);

    columns: PrimeTableColumn[] = [
        { field: 'doctor_name', header: 'table_name', sortable: true, filter: true },
        { field: 'crm', header: 'table_crm', sortable: true, filter: true },
        { field: 'tel', header: 'table_phone', filter: true },
        { field: 'email', header: 'table_email', filter: true },
        { field: 'status', header: 'table_status', sortable: true, filter: true },
        { field: 'actions', header: 'table_action', width: '100px', styleClass: 'text-center', type: 'action', filter: false }
    ];

    templates: { [key: string]: TemplateRef<any> } = {};

    search$ = new BehaviorSubject<string>('');
    /** Speciality ids chosen in the filter above the table; empty = no filter. */
    specialtyIds$ = new BehaviorSubject<number[]>([]);
    selectedSpecialtyIds: number[] = [];
    specialties: any[] = [];
    specialityGroups: GroupedSpecialities = { grouped: false, groups: [] };
    /** specialityGroups with the current selection pulled into a leading "Selected" bucket. */
    specialityOptions: GroupedSpecialities = { grouped: false, groups: [] };
    page$ = new BehaviorSubject<number>(1);
    pageSize = 10;

    // For manual refresh trigger
    private refresh$ = new BehaviorSubject<void>(undefined);
    private isManualRefresh = false;

    // Use string type for search term to match template binding
    searchTerm: string = '';

    // True while the delete-doctor edge function call is in flight (shows overlay).
    isDeleting = false;

    loading$ = new BehaviorSubject<boolean>(true);
    doctorsData$ = combineLatest([
        this.search$.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            tap(() => this.page$.next(1))
        ),
        this.specialtyIds$.pipe(
            distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
            tap(() => this.page$.next(1))
        ),
        this.page$,
        this.refresh$
    ]).pipe(
        switchMap(([search, specialtyIds, page]) => {
            this.isManualRefresh = false;
            this.loading$.next(true);
            const offset = (page - 1) * this.pageSize;

            return this.fetchDoctors(search, page, offset, specialtyIds);
        }),
        tap((result) => {
            this.loading$.next(false);
        }),
        startWith({ data: [], count: 0 })
    );


    private currentData: any = null;
    private currentCount: number = 0;

    ngOnInit() {
        // Subscribe to global refresh
        this.subs.add(this.professionalsService.refreshDoctors$.subscribe(() => {
            this.refresh();
        }));
        // Speciality filter options (visible specialities of the branch, grouped)
        this.subs.add(this.store.select(selectBranchId).subscribe(branchId => {
            if (branchId) this.loadSpecialties(branchId);
        }));
    }

    private loadSpecialties(branchId: number) {
        this.subs.add(this.settingsService.getSpecialties(branchId, '', 1000, 0, true).subscribe({
            next: res => {
                this.specialties = res.data || [];
                this.specialityGroups = groupSpecialities(
                    this.specialties,
                    this.translate.instant('speciality_group_ungrouped_bucket')
                );
                this.refreshSpecialityOptions();
            },
            error: err => console.error('Error loading specialties for filter', err)
        }));
    }

    onSpecialtyFilterChange(ids: number[] | null) {
        this.selectedSpecialtyIds = ids || [];
        this.refreshSpecialityOptions();
        this.specialtyIds$.next(this.selectedSpecialtyIds);
    }

    private refreshSpecialityOptions() {
        this.specialityOptions = withSelectedOnTop(
            this.specialityGroups,
            this.selectedSpecialtyIds,
            (i: any) => i.id,
            this.translate.instant('speciality_group_selected')
        );
    }

    clearSpecialtyFilter() {
        this.onSpecialtyFilterChange([]);
    }

    isSpecialityGroupSelected(group: SpecialityGroupOption): boolean {
        return isGroupFullySelected(group, this.selectedSpecialtyIds, (i: any) => i.id);
    }

    toggleSpecialityGroup(group: SpecialityGroupOption): void {
        this.onSpecialtyFilterChange(toggleGroupSelection(group, this.selectedSpecialtyIds, (i: any) => i.id));
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.templates = {
                doctor_name: this.doctorNameTemplate,
                status: this.statusTemplate,
                actions: this.actionsTemplate
            };
        });
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    async fetchDoctors(search: string, page: number, offset: number, specialtyIds: number[] = []) {
        try {
            const { data, error } = await this.professionalsService.getProfessionals(
                this.pageSize,
                offset,
                search,
                specialtyIds
            );

            if (error || !data) {
                console.error('Error fetching doctors', error);
                return { data: [], count: 0 };
            }

            return { data: data.doctors || [], count: data.total_pages * this.pageSize }; // Backend returns total_pages, estimating count
        } catch (e) {
            console.error('Exception fetching doctors', e);
            return { data: [], count: 0 };
        }
    }

    onSearch(term: string) {
        this.search$.next(term);
    }

    onPageChange(page: number) {
        this.page$.next(page);
    }

    onNewDoctor() {
        this.newDoctor.emit();
    }

    onEdit(doctor: Professional) {
        this.editDoctor.emit(doctor);
    }

    onView(doctor: Professional) {
        this.router.navigate(['/professionals', doctor.doctor_id]);
    }

    async toggleStatus(doctor: Professional) {
        try {
            const { error } = await this.professionalsService.toggleDoctorStatus(
                doctor.doctor_id, 
                doctor.status,
                (doctor as any).user_id
            );
            
            if (error) {
                console.error('Error updating status', error);
                toast.error(this.translate.instant('settings_doctor_error_update'));
            } else {
                toast.success(this.translate.instant('settings_doctor_success_update'));
                this.refresh();
            }
        } catch (err) {
            console.error('Error in toggle status', err);
            toast.error(this.translate.instant('error_update_status'));
        }
    }

    deleteDoctor(doctor: Professional) {
        this.confirmationService.confirm({
            message: this.translate.instant('doctors_delete_confirm_message', {
                name: doctor.doctor_name,
            }),
            header: this.translate.instant('doctors_delete_confirm_title'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('YES'),
            rejectLabel: this.translate.instant('NO'),
            accept: async () => {
                this.isDeleting = true;
                try {
                    const result = await this.professionalsService.deleteDoctor(doctor.doctor_id);
                    if (result?.success) {
                        toast.success(this.translate.instant('doctors_delete_success'));
                        this.refresh();
                    } else if (result?.reason === 'has_linked_data') {
                        toast.error(
                            this.translate.instant('doctors_delete_blocked', {
                                name: doctor.doctor_name,
                                reasons: this.formatBlockers(result.blockers),
                            }),
                        );
                    } else if (result?.reason === 'forbidden') {
                        toast.error(this.translate.instant('doctors_delete_forbidden'));
                    } else {
                        toast.error(this.translate.instant('doctors_delete_error'));
                    }
                } catch (err) {
                    console.error('Error deleting doctor', err);
                    toast.error(this.translate.instant('doctors_delete_error'));
                } finally {
                    this.isDeleting = false;
                }
            },
        });
    }

    /** Map blocker codes returned by the edge function to translated, comma-joined labels. */
    private formatBlockers(blockers: string[] | undefined | null): string {
        if (!blockers?.length) return '';
        return blockers
            .map((b) => this.translate.instant('blocker_' + b))
            .join(', ');
    }

    refresh() {
        this.isManualRefresh = true;
        this.refresh$.next();
    }
}
